import React, { createContext, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiWorker, getNodeWithFallback } from '../api/worker';
import { injectNodeTheme } from '../utils/theme';
import { Node, NodeConfig } from '../types';
import { PUBLIC_PLATFORM_PATHS, TERMINOLOGY_DEFAULTS } from '../constants';
import { SHOULD_USE_MOCKS } from '../lib/clerk';
import { BRANDS, THEMES } from '../api/branding';
import { MOCK_STATE_KEYS } from '../api/mockData';
import toast from 'react-hot-toast';
import { useSeasonalTheme } from '../hooks/useSeasonalTheme';

interface TenantContextType {
  node: Node | null;
  lineage: Node[];
  isGlobal: boolean; // Platform brand level
  isUniversal: boolean; // Aggregated user dashboard view
  isLoading: boolean;
  slug: string;
  activeOrg: string | null;
  userThemePref: 'light' | 'dark' | 'system';
  setThemePref: (pref: 'light' | 'dark' | 'system') => void;
  colorMode: 'light' | 'dark';
  activeBrandId: string;
  brand: {
    name: string;
    logoUrl?: string;
    primary: string;
    secondary: string;
  };
  getTerm: (key: keyof NonNullable<NodeConfig['terminology']>) => string;
  clearActiveOrg: (redirectTo?: string) => void;
  switchOrg: (targetSlug: string, redirectTo?: string) => void;
}

const TenantCtx = createContext<TenantContextType | null>(null);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const season = useSeasonalTheme();
  const [userThemePref, setUserThemePref] = React.useState<'light' | 'dark' | 'system'>(() => {
    return (localStorage.getItem('theme_preference') as ('light' | 'dark' | 'system')) || 'system';
  });

  const setThemePref = (pref: 'light' | 'dark' | 'system') => {
    setUserThemePref(pref);
    localStorage.setItem('theme_preference', pref);
  };

  const resolvedThemeId = React.useMemo(() => {
    if (userThemePref === 'light') return 'light';
    if (userThemePref === 'dark') return 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }, [userThemePref]);

  const navigate = useNavigate();
  const location = useLocation();

  const [activeOrg, setActiveOrg] = React.useState<string | null>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlOrgParam = searchParams.get('org');
    const currentPath = window.location.pathname;
    const isCurrentlyOnPublicPath = PUBLIC_PLATFORM_PATHS.some(
      p => p === '/' ? currentPath === '/' : currentPath.startsWith(p)
    );
    return isCurrentlyOnPublicPath ? null : (urlOrgParam || localStorage.getItem('active_org'));
  });

  useEffect(() => {
    const isCurrentlyOnPublicPath = PUBLIC_PLATFORM_PATHS.some(
      p => p === '/' ? location.pathname === '/' : location.pathname.startsWith(p)
    );
    if (isCurrentlyOnPublicPath) {
      if (activeOrg !== null) {
        queueMicrotask(() => setActiveOrg(null));
      }
    } else {
      const stored = localStorage.getItem('active_org');
      if (stored && stored !== activeOrg) {
        queueMicrotask(() => setActiveOrg(stored));
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlOrgParam = searchParams.get('org');
    if (urlOrgParam && urlOrgParam !== activeOrg) {
      localStorage.setItem('active_org', urlOrgParam);
      // Defer the state write off the effect's synchronous path; the URL rewrite
      // below keeps this a one-shot deep-link normalization.
      queueMicrotask(() => setActiveOrg(urlOrgParam));
      searchParams.delete('org');
      const newSearch = searchParams.toString();
      navigate({ pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' }, { replace: true });
    }
  }, [location.search, activeOrg, navigate, location.pathname]);

  const orgParam = activeOrg;
  const isUniversal = orgParam === 'universal';

  const shouldAllowOverrides = SHOULD_USE_MOCKS || import.meta.env.VITE_ENABLE_DEVTOOLS === 'true';
  const forcedThemeId = shouldAllowOverrides ? localStorage.getItem('mock_theme_id') : null;

  const platformBrand = BRANDS['orgsets.com'];
  const isPlatformRoot = !orgParam;
  const slug = orgParam || 'global';

  const getPlatformNode = (forcedTheme: string | null): { node: Node, lineage: Node[] } => {
    const brandInfo = BRANDS['orgsets.com'];
    
    const brandThemeId = forcedTheme || brandInfo.defaultThemeId;
    const themeConfig = THEMES[brandThemeId] || THEMES.bauhaus;
    
    const platformNode: Node = {
      id: 'platform-root',
      name: brandInfo.name,
      slug: 'global',
      tier: 'INSTITUTIONAL',
      visibility: 'PUBLIC',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      modules: brandInfo.config.modules || [],
      config: {
        ...brandInfo.config,
        ...themeConfig,
        themeId: brandThemeId
      } as any
    };
    return { node: platformNode, lineage: [] };
  };

  const { data: tenantData, isLoading } = useQuery({
    queryKey: ['node-config-full', slug],
    queryFn: async () => {
      // For 'global' or 'universal' platform level, we resolve a virtual node based on the brand
      if (slug === 'global' || slug === 'universal' || !slug) {
        return getPlatformNode(forcedThemeId);
      }

      const targetSlug = slug;
      
      try {
        // Fetch the primary node with our unified fallback helper
        const node = await getNodeWithFallback(targetSlug);
        
        if (forcedThemeId && THEMES[forcedThemeId]) {
          node.config = {
            ...(node.config || {}),
            themeId: forcedThemeId,
            ...THEMES[forcedThemeId]
          };
        }

        // Fetch lineage nodes for inheritance
        let lineageNodes: Node[] = [];
        if (node.lineageIds && node.lineageIds.length > 0) {
          try {
            const lineageResponse = await apiWorker.get(`/api/nodes/${node.id}/lineage`);
            lineageNodes = lineageResponse.data && Array.isArray(lineageResponse.data.content) 
              ? lineageResponse.data.content 
              : (lineageResponse.data || []);
          } catch (err) {
            console.warn(`Failed to fetch lineage for node ${node.id}:`, err);
          }
        }
        
        return { node, lineage: lineageNodes };
      } catch (e: any) {
        console.warn('Critical failure in node branding resolution:', e);
        
        if (e?.response?.status === 404) {
          toast.error(`Organization '${targetSlug}' not found.`);
          // If the cached active_org is dead, remove it to prevent getting stuck
          if (localStorage.getItem('active_org') === targetSlug) {
            localStorage.removeItem('active_org');
          }
        } else if (!e?.isAvailabilityError) {
          // We don't double-toast if it's the global availability error (AuthContext handles that)
          toast.error('Failed to load organization settings.');
        }
        
        return { node: null, lineage: [] };
      }
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
    retry: false,
    initialData: () => {
      if (slug === 'global' || slug === 'universal' || !slug) {
        return getPlatformNode(forcedThemeId);
      }
      return undefined;
    }
  });

  const node = tenantData?.node || null;
  const lineage = tenantData?.lineage || [];

  // Resolved brand for the entire app
  const brand = React.useMemo(() => {
    const activeBrandId = node?.config?.themeId || platformBrand.defaultThemeId;
    const themeConfig = THEMES[activeBrandId] || THEMES.bauhaus;
    return {
      name: platformBrand.name,
      logoUrl: platformBrand.config?.logoUrl,
      primary: node?.config?.primaryColor || themeConfig.primaryColor || platformBrand.config?.primaryColor,
      secondary: node?.config?.secondaryColor || themeConfig.secondaryColor || platformBrand.config?.secondaryColor,
    };
  }, [platformBrand, node]);

  // Resolved terminology helper
  const getTerm = React.useCallback((key: keyof NonNullable<NodeConfig['terminology']>): string => {
    if (!node) return TERMINOLOGY_DEFAULTS[key as string] || '';
    
    const config = node.config || {} as any;
    const enforceParent = config?.features?.enforceParentTerminology;

    if (enforceParent && lineage.length > 0) {
      // Use the root parent's terminology
      const rootParent = lineage[0];
      return rootParent.config?.terminology?.[key] || TERMINOLOGY_DEFAULTS[key as string];
    }

    // Standard resolution: local > parent > global default
    const localTerm = config?.terminology?.[key as any];
    if (localTerm) return localTerm;

    // Search up the lineage
    for (let i = lineage.length - 1; i >= 0; i--) {
      const parentTerm = lineage[i].config?.terminology?.[key];
      if (parentTerm) return parentTerm;
    }

    return TERMINOLOGY_DEFAULTS[key as string];
  }, [node, lineage]);


  useEffect(() => {
    const brandKey = 'orgsets.com';
    const brandInfo = BRANDS['orgsets.com'];
    const activeThemeId = node?.config?.themeId || brandInfo.defaultThemeId;
    const themeConfig = THEMES[activeThemeId] || THEMES.bauhaus;

    // Merge branding colors up the lineage if missing locally
    const resolvedConfig: any = { 
      ...themeConfig,
      ...(node?.config || brandInfo.config),
      primaryColor: node?.config?.primaryColor || themeConfig.primaryColor || brandInfo.config?.primaryColor,
      secondaryColor: node?.config?.secondaryColor || themeConfig.secondaryColor || brandInfo.config?.secondaryColor,
      themeId: activeThemeId
    };
    
    injectNodeTheme(resolvedConfig, resolvedThemeId as 'light' | 'dark');
    
    // Always apply theme and style attributes to root element
    document.documentElement.setAttribute('data-theme', resolvedThemeId);
    document.documentElement.setAttribute('data-style', activeThemeId);
    document.documentElement.setAttribute('data-brand', brandKey);
    
    const enableSeasonalAccents = node?.config?.features?.enableSeasonalAccents !== false; // Default true
    if (enableSeasonalAccents && season) {
      document.documentElement.setAttribute('data-season', season);
    } else {
      document.documentElement.removeAttribute('data-season');
    }
    
    // For org-member pages (not the platform root), set document.title to the
    // org node name so browser tabs/history reflect the active org.
    // For platform-root/public marketing pages, leave document.title alone —
    // the <SEO> component (via react-helmet-async) handles those titles using
    // the domain-level brand name (e.g. "OrgSets", "OrgWrangler"), ensuring
    // public pages are never titled after an org node.
    const currentPath = window.location.pathname;
    const isPublicPath = isPlatformRoot || PUBLIC_PLATFORM_PATHS.some(
      p => p === '/' ? currentPath === '/' : currentPath.startsWith(p)
    );
    if (!isPublicPath) {
      const brandName = node?.name || platformBrand.name;
      if (isUniversal) {
        document.title = `Universal Dashboard | ${brandName}`;
      } else {
        document.title = brandName;
      }
    }
  }, [node, lineage, isUniversal, isPlatformRoot, platformBrand, resolvedThemeId, season]);

  const activeThemeId = node?.config?.themeId || BRANDS['orgsets.com'].defaultThemeId;

  const clearActiveOrg = React.useCallback((redirectTo?: string) => {
    localStorage.removeItem('active_org');
    setActiveOrg(null);
    const target = redirectTo || (location.pathname.startsWith('/system-admin') ? '/system-admin' : '/dashboard');
    navigate(target);
  }, [navigate, location.pathname]);

  const switchOrg = React.useCallback((targetSlug: string, redirectTo: string = '/dashboard') => {
    if (!targetSlug) return;
    localStorage.setItem('active_org', targetSlug);
    setActiveOrg(targetSlug);
    navigate(redirectTo);
  }, [navigate]);

  const value = React.useMemo(() => ({
    node,
    lineage,
    isGlobal: isPlatformRoot,
    isUniversal,
    isLoading,
    slug,
    activeOrg,
    userThemePref,
    setThemePref,
    colorMode: resolvedThemeId as 'light' | 'dark',
    activeBrandId: activeThemeId,
    brand,
    getTerm,
    clearActiveOrg,
    switchOrg
  }), [node, lineage, isPlatformRoot, isUniversal, isLoading, slug, activeOrg, brand, getTerm, clearActiveOrg, switchOrg, userThemePref, setThemePref, resolvedThemeId, activeThemeId]);

  return (
    <TenantCtx.Provider value={value}>
      {children}
    </TenantCtx.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantCtx);
  if (!context) throw new Error('useTenant must be used within a TenantProvider');
  return context;
};
